// src/components/ui/AvatarPicker/AvatarPicker.tsx
import React, {useCallback, useRef, useState} from 'react';
import {IonIcon} from '@ionic/react';
import {checkmarkCircle, cloudUploadOutline, logoGoogle} from 'ionicons/icons';
import {supabase} from '../../../services/supabase';
import {syncGoogleAvatar} from '../../../services/avatar';
import {Segment, type SegmentOption} from '../Segment';
import {IconPicker, type IconValue} from '../IconPicker';
import {Button} from '../Button';

// ═══════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════

export type AvatarType = 'icon' | 'upload' | 'google';

export interface AvatarValue {
    type: AvatarType;
    value: string; // emoji/icon name for 'icon', URL for 'upload'/'google'
    iconType?: 'ionic' | 'mdi' | 'lucide' | 'emoji' | 'upload' | 'url'; // for icon type
    imageUrl?: string; // for upload/url icon types
}

export interface AvatarPickerProps {
    /** Currently selected avatar */
    value?: AvatarValue | null;
    /** Change handler */
    onChange: (avatar: AvatarValue) => void;
    /** User ID for upload path */
    userId?: string;
    /** Google avatar URL (from user profile) */
    googleAvatarUrl?: string | null;
    /** Storage bucket name */
    storageBucket?: string;
    /** Default tab to show */
    defaultTab?: AvatarType;
    /** Maximum file size in MB */
    maxFileSizeMB?: number;
    /** Accepted image types */
    acceptedTypes?: string[];
    /** Custom className */
    className?: string;
    /** Whether upload is in progress */
    uploading?: boolean;
}

// ═══════════════════════════════════════════════════════════════════
// AVATAR PICKER COMPONENT
// ═══════════════════════════════════════════════════════════════════

type TabValue = 'icon' | 'upload' | 'google';

const TAB_OPTIONS: SegmentOption<TabValue>[] = [
    {value: 'icon', label: 'Icon'},
    {value: 'upload', label: 'Upload'},
    {value: 'google', label: 'Google'},
];

export const AvatarPicker: React.FC<AvatarPickerProps> = ({
                                                              value,
                                                              onChange,
                                                              userId,
                                                              googleAvatarUrl,
                                                              storageBucket = 'avatars',
                                                              defaultTab = 'icon',
                                                              maxFileSizeMB = 2,
                                                              acceptedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
                                                              className,
                                                          }) => {
    // Determine initial tab based on current value type, or fall back to defaultTab
    const getInitialTab = (): TabValue => {
        if (value?.type) {
            // For google type, only show google tab if googleAvatarUrl is available
            if (value.type === 'google' && !googleAvatarUrl) {
                return 'upload'; // Fall back to upload for stored google images
            }
            return value.type;
        }
        return defaultTab;
    };

    const [activeTab, setActiveTab] = useState<TabValue>(getInitialTab);
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [uploadPreview, setUploadPreview] = useState<string | null>(null);
    const [googleLoading, setGoogleLoading] = useState(false);
    const [googleError, setGoogleError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Update active tab when value changes (e.g., when modal opens with different avatar)
    React.useEffect(() => {
        if (value?.type) {
            // For google type, only switch if googleAvatarUrl is available
            if (value.type === 'google' && !googleAvatarUrl) {
                setActiveTab('upload');
            } else {
                setActiveTab(value.type);
            }
        }
    }, [value?.type, googleAvatarUrl]);

    // Filter tabs based on available options
    const availableTabs = TAB_OPTIONS.filter(tab => {
        if (tab.value === 'google') {
            return !!googleAvatarUrl;
        }
        return true;
    });

    // Handle icon selection
    const handleIconSelect = useCallback((icon: IconValue) => {
        onChange({
            type: 'icon',
            value: icon.name,
            iconType: icon.type,
            imageUrl: icon.imageUrl, // For upload/url icon types
        });
    }, [onChange]);

    // Handle file selection
    const handleFileSelect = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Validate file type
        if (!acceptedTypes.includes(file.type)) {
            setUploadError(`Invalid file type. Accepted: ${acceptedTypes.map(t => t.split('/')[1]).join(', ')}`);
            return;
        }

        // Validate file size
        if (file.size > maxFileSizeMB * 1024 * 1024) {
            setUploadError(`File too large. Maximum: ${maxFileSizeMB}MB`);
            return;
        }

        setUploadError(null);
        setUploading(true);

        try {
            // Create preview
            const reader = new FileReader();
            reader.onload = (e) => setUploadPreview(e.target?.result as string);
            reader.readAsDataURL(file);

            // Generate unique filename
            const fileExt = file.name.split('.').pop()?.toLowerCase() || 'jpg';
            const fileName = `${userId || 'anonymous'}/${Date.now()}-avatar.${fileExt}`;

            // Upload to Supabase Storage
            const {data, error} = await supabase.storage
                .from(storageBucket)
                .upload(fileName, file, {
                    cacheControl: '3600',
                    upsert: true,
                });

            if (error) {
                throw error;
            }

            // Get public URL
            const {data: {publicUrl}} = supabase.storage
                .from(storageBucket)
                .getPublicUrl(data.path);

            onChange({
                type: 'upload',
                value: publicUrl,
            });
        } catch (err) {
            console.error('Upload error:', err);
            setUploadError('Failed to upload image. Please try again.');
            setUploadPreview(null);
        } finally {
            setUploading(false);
        }

        // Reset input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }, [userId, storageBucket, maxFileSizeMB, acceptedTypes, onChange]);

    // Handle Google avatar selection - syncs to Supabase Storage via edge function
    const handleGoogleSelect = useCallback(async () => {
        if (!googleAvatarUrl || !userId) {
            setGoogleError('Unable to use Google photo. Please try uploading instead.');
            return;
        }

        setGoogleLoading(true);
        setGoogleError(null);

        try {
            // Sync Google avatar to Supabase Storage (uses edge function, falls back to client-side)
            // Pass null for storedGoogleSource to force a fresh sync
            const syncResult = await syncGoogleAvatar(userId, googleAvatarUrl, null, null);

            if (syncResult) {
                onChange({
                    type: 'google',
                    value: syncResult.avatarUrl, // Use the Supabase Storage URL, not the Google URL
                });
            } else {
                setGoogleError('Failed to save Google photo. Please try again or upload manually.');
            }
        } catch (err) {
            console.error('Error storing Google avatar:', err);
            setGoogleError('Failed to save Google photo. Please try again.');
        } finally {
            setGoogleLoading(false);
        }
    }, [googleAvatarUrl, userId, onChange]);

    // Trigger file input
    const triggerFileInput = () => {
        fileInputRef.current?.click();
    };

    // Get current preview URL
    const getCurrentPreview = (): string | null => {
        if (value?.type === 'upload' || value?.type === 'google') {
            return value.value;
        }
        if (uploadPreview && activeTab === 'upload') {
            return uploadPreview;
        }
        return null;
    };

    // Check if current value matches a type
    const isSelected = (type: AvatarType) => value?.type === type;

    return (
        <div className={className} style={{display: 'flex', flexDirection: 'column', gap: 16}}>
            {/* Tab selector */}
            {availableTabs.length > 1 && (
                <Segment
                    options={availableTabs}
                    value={activeTab}
                    onChange={setActiveTab}
                    size="sm"
                    variant="minimal"
                    fullWidth
                />
            )}

            {/* Icon Tab */}
            {activeTab === 'icon' && (
                <div>
                    {/* Current selection preview */}
                    {value?.type === 'icon' && (
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 12,
                            marginBottom: 12,
                            padding: 12,
                            background: 'var(--ion-color-step-50)',
                            borderRadius: 8,
                        }}>
                            <div style={{
                                width: 48,
                                height: 48,
                                borderRadius: '50%',
                                background: 'var(--kff-forge-gold)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: 28,
                            }}>
                                {value.value}
                            </div>
                            <span style={{
                                fontFamily: 'var(--kff-data-font)',
                                fontSize: 13,
                                color: 'var(--ion-color-medium)',
                            }}>
                Current selection
              </span>
                        </div>
                    )}

                    <IconPicker
                        value={value?.type === 'icon' ? {type: value.iconType || 'emoji', name: value.value} : null}
                        onChange={handleIconSelect}
                        defaultTab="emoji"
                        tabs={['emoji', 'ionic', 'mdi', 'lucide']}
                        iconSize={28}
                        maxHeight={200}
                    />
                </div>
            )}

            {/* Upload Tab */}
            {activeTab === 'upload' && (
                <div style={{display: 'flex', flexDirection: 'column', gap: 12}}>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept={acceptedTypes.join(',')}
                        onChange={handleFileSelect}
                        style={{display: 'none'}}
                    />

                    {/* Preview */}
                    {getCurrentPreview() && isSelected('upload') && (
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: 16,
                        }}>
                            <div style={{
                                position: 'relative',
                                width: 96,
                                height: 96,
                            }}>
                                <img
                                    src={getCurrentPreview()!}
                                    alt="Avatar preview"
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        borderRadius: '50%',
                                        objectFit: 'cover',
                                        border: '3px solid var(--kff-forge-gold)',
                                    }}
                                />
                                <IonIcon
                                    icon={checkmarkCircle}
                                    style={{
                                        position: 'absolute',
                                        bottom: 0,
                                        right: 0,
                                        fontSize: 24,
                                        color: 'var(--kff-timberline-green)',
                                        background: 'white',
                                        borderRadius: '50%',
                                    }}
                                />
                            </div>
                        </div>
                    )}

                    {/* Upload button */}
                    <Button
                        fill="outline"
                        expand="block"
                        iconStart={cloudUploadOutline}
                        onClick={triggerFileInput}
                        disabled={uploading}
                    >
                        {uploading ? 'Uploading...' : 'Choose Image'}
                    </Button>

                    {/* Error message */}
                    {uploadError && (
                        <div style={{
                            padding: 12,
                            background: 'rgba(252, 165, 165, 0.15)',
                            border: '1px solid var(--ff-expense)',
                            borderRadius: 8,
                            color: 'var(--ff-expense)',
                            fontFamily: 'var(--kff-data-font)',
                            fontSize: 13,
                        }}>
                            {uploadError}
                        </div>
                    )}

                    {/* Help text */}
                    <div style={{
                        fontFamily: 'var(--kff-data-font)',
                        fontSize: 12,
                        color: 'var(--ion-color-medium)',
                        textAlign: 'center',
                    }}>
                        Max {maxFileSizeMB}MB. Accepted: JPG, PNG, GIF, WebP
                    </div>
                </div>
            )}

            {/* Google Tab */}
            {activeTab === 'google' && googleAvatarUrl && (
                <div style={{display: 'flex', flexDirection: 'column', gap: 16, alignItems: 'center'}}>
                    <div style={{
                        position: 'relative',
                        width: 96,
                        height: 96,
                    }}>
                        <img
                            src={googleAvatarUrl}
                            alt="Google profile"
                            style={{
                                width: '100%',
                                height: '100%',
                                borderRadius: '50%',
                                objectFit: 'cover',
                                border: isSelected('google') ? '3px solid var(--kff-forge-gold)' : '3px solid transparent',
                                opacity: googleLoading ? 0.5 : 1,
                            }}
                        />
                        {isSelected('google') && !googleLoading && (
                            <IonIcon
                                icon={checkmarkCircle}
                                style={{
                                    position: 'absolute',
                                    bottom: 0,
                                    right: 0,
                                    fontSize: 24,
                                    color: 'var(--kff-timberline-green)',
                                    background: 'white',
                                    borderRadius: '50%',
                                }}
                            />
                        )}
                    </div>

                    <div style={{
                        fontFamily: 'var(--kff-data-font)',
                        fontSize: 13,
                        color: 'var(--ion-color-medium)',
                        textAlign: 'center',
                    }}>
                        {googleLoading ? 'Saving to your account...' : 'Your Google profile picture'}
                    </div>

                    {/* Error message */}
                    {googleError && (
                        <div style={{
                            padding: 12,
                            background: 'rgba(252, 165, 165, 0.15)',
                            border: '1px solid var(--ion-color-danger)',
                            borderRadius: 8,
                            color: 'var(--ion-color-danger)',
                            fontFamily: 'var(--kff-data-font)',
                            fontSize: 13,
                            textAlign: 'center',
                        }}>
                            {googleError}
                        </div>
                    )}

                    <Button
                        variant="primary"
                        fill={isSelected('google') ? 'solid' : 'outline'}
                        iconStart={logoGoogle}
                        onClick={handleGoogleSelect}
                        disabled={googleLoading}
                    >
                        {googleLoading ? 'Saving...' : isSelected('google') ? 'Selected' : 'Use Google Photo'}
                    </Button>
                </div>
            )}
        </div>
    );
};

AvatarPicker.displayName = 'AvatarPicker';

// ═══════════════════════════════════════════════════════════════════
// HELPER: Avatar Display Component
// ═══════════════════════════════════════════════════════════════════

export interface AvatarDisplayProps {
    avatar: AvatarValue | null | undefined;
    size?: number;
    fallback?: string;
    backgroundColor?: string;
    className?: string;
    style?: React.CSSProperties;
}

export const AvatarDisplay: React.FC<AvatarDisplayProps> = ({
                                                                avatar,
                                                                size = 40,
                                                                fallback = '👤',
                                                                backgroundColor = 'var(--kff-foundry-blue)',
                                                                className,
                                                                style,
                                                            }) => {
    if (!avatar) {
        return (
            <div
                className={className}
                style={{
                    width: size,
                    height: size,
                    borderRadius: '50%',
                    backgroundColor,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: size * 0.5,
                    flexShrink: 0,
                    ...style,
                }}
            >
                {fallback}
            </div>
        );
    }

    if (avatar.type === 'icon') {
        return (
            <div
                className={className}
                style={{
                    width: size,
                    height: size,
                    borderRadius: '50%',
                    backgroundColor,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: size * 0.5,
                    flexShrink: 0,
                    ...style,
                }}
            >
                {avatar.value}
            </div>
        );
    }

    // Upload or Google - render as image
    return (
        <img
            src={avatar.value}
            alt="Avatar"
            className={className}
            style={{
                width: size,
                height: size,
                borderRadius: '50%',
                objectFit: 'cover',
                flexShrink: 0,
                ...style,
            }}
            onError={(e) => {
                // Fallback to default on error
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
            }}
        />
    );
};

AvatarDisplay.displayName = 'AvatarDisplay';

export default AvatarPicker;
